import React, { useState, useEffect } from 'react';
import { users } from '../../services/apiService';
import { useAuth } from '../../hooks/useAuth';
import { TextField, DatePicker, Autocomplete } from '@anyemp/ui-kit';
import { MessageSquare, Plus, AlertCircle, X } from 'lucide-react';

const CommunicationsManager = ({
  entityId,
  entityType = 'Talent', // 'Talent' или 'JobApplication'
  communications = [],
  onCommunicationsChange,
  apiMethods = null, // Объект с методами API для работы с коммуникациями
  showTitle = true,
  title = "Communications",
  className = ""
}) => {
  const { user } = useAuth();
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Загружаем пользователей при монтировании компонента
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await users.getAllUsers();
      
      // Обрабатываем структуру ответа: response.data.users
      let usersData = [];
      if (response.success && response.data && response.data.users) {
        usersData = Array.isArray(response.data.users) ? response.data.users : [];
      } else if (response.data && Array.isArray(response.data)) {
        usersData = response.data;
      } else if (Array.isArray(response)) {
        usersData = response;
      }
      
      setUsersList(usersData);
    } catch (err) {
      console.error('Error fetching users:', err);
      setUsersList([]); // Устанавливаем пустой массив в случае ошибки
    }
  };

  const addCommunication = () => {
    const newCommunication = {
      id: `temp-${Date.now()}`, // Временный строковый ID
      // По умолчанию менеджер — текущий пользователь; в форме его можно сменить
      manager_id: user?.id || '',
      notes: '',
      follow_up_date: '',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    onCommunicationsChange([...communications, newCommunication]);
  };

  const removeCommunication = (index) => {
    onCommunicationsChange(communications.filter((_, i) => i !== index));
  };

  const updateCommunication = (index, field, value) => {
    const updatedCommunications = communications.map((communication, i) => 
      i === index ? { ...communication, [field]: value } : communication
    );
    onCommunicationsChange(updatedCommunications);
  };

  const isOverdue = (followUpDate) => {
    if (!followUpDate) return false;
    return new Date(followUpDate) < new Date();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US');
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-US');
  };

  const getUserName = (userId) => {
    const user = usersList.find(u => u.id === userId);
    return user ? (user.name || user.email || user.full_name || `User ${userId}`) : `User ${userId}`;
  };

  const getUserAvatar = (userId) => {
    const user = usersList.find(u => u.id === userId);
    return user?.avatar;
  };

  return (
    <div className={className}>
      {showTitle && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h3 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary flex items-center">
            <MessageSquare className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-400" />
            {title}
          </h3>
          <button
            type="button"
            onClick={addCommunication}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Communication
          </button>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-red-400 dark:text-red-300 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
              <p className="mt-1 text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
            <button
              type="button"
              onClick={() => setError(null)}
              className="ml-3 inline-flex items-center justify-center h-5 w-5 text-red-400 dark:text-red-300 hover:text-red-600 dark:hover:text-red-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      
      {communications.length === 0 ? (
        <div className="text-center py-8">
          <MessageSquare className="h-12 w-12 text-light-text-tertiary dark:text-dark-text-tertiary mx-auto mb-4" />
          <p className="mt-2 text-sm text-light-text-secondary dark:text-dark-text-secondary">No communications added</p>
          <p className="mt-1 text-sm text-light-text-tertiary dark:text-dark-text-tertiary">Click "Add Communication" to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {communications.map((communication, index) => (
            <div key={communication.id || index} className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-lg p-4 border border-light-border-primary dark:border-dark-border-primary">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-md font-medium text-light-text-primary dark:text-dark-text-primary">Communication {index + 1}</h4>
                <button
                  type="button"
                  onClick={() => removeCommunication(index)}
                  className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Autocomplete
                    clearable
                    fullWidth
                    label="Manager *"
                    value={communication.manager_id != null ? String(communication.manager_id) : ''}
                    onChange={(value) => updateCommunication(index, 'manager_id', value ? Number(value) : '')}
                    placeholder="Select manager"
                    options={usersList && Array.isArray(usersList) ? usersList.map((user) => ({
                      value: String(user.id),
                      label: user.name || user.email || user.full_name || `User ${user.id}`
                    })) : []}
                  />
                </div>

                <div>
                  <DatePicker
                    label="Follow-up Date"
                    size="small"
                    fullWidth
                    value={communication.follow_up_date ? new Date(communication.follow_up_date) : null}
                    onChange={(d) => updateCommunication(index, 'follow_up_date', d ? d.toISOString().slice(0, 10) : '')}
                  />
                </div>

                <div className="md:col-span-2">
                  <TextField
                    label="Notes"
                    required
                    size="small"
                    multiline
                    rows={3}
                    fullWidth
                    value={communication.notes || ''}
                    onChange={(e) => updateCommunication(index, 'notes', e.target.value)}
                    placeholder="Enter communication notes..."
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Overdue Communications Alert */}
      {communications.some(comm => comm.follow_up_date && isOverdue(comm.follow_up_date)) && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mt-4">
          <div className="flex items-center">
            <AlertCircle className="text-red-600 dark:text-red-400 text-lg mr-2" />
            <div className="text-red-800 dark:text-red-200">
              <strong>Overdue Communications:</strong> You have communications requiring follow-up contact.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunicationsManager;
